// The settings panel: every definition of a settings model as a control (a range as a slider, a toggle as a switch, a
// choice as a radio row), grouped by `group` in the order the game gives. Labels are string ids from the definitions.
import { h, type VNode } from "preact";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;
import { Choice, Group, Slider, Toggle } from "./components";
import type { ScreenContext } from "./app";
import type { SettingDef, Settings } from "./settings";

/** A settings panel's options. */
export interface SettingsPanelProps {
  ctx: ScreenContext;
  settings: Settings;
  /** The groups to show, in order, with their heading's string id. Settings in other groups are left out. */
  groups: readonly { id: string; label: string }[];
  /** A range's value as the player reads it ("80%"); default: the number. */
  format?: (def: SettingDef & { kind: "range" }, value: number) => string;
  /** String ids for a toggle's state text. */
  on: string;
  off: string;
}

/** Every setting as a control, by group. */
export function SettingsPanel(p: SettingsPanelProps): El {
  const { ctx, settings } = p;
  const t = ctx.t;
  const set = (id: string, v: number | boolean | string): void => {
    settings.set(id, v);
    ctx.ui.refresh();
  };
  const control = (d: SettingDef): El => {
    const id = `setting-${d.id.replace(/[^a-z0-9-]/gi, "-")}`;
    switch (d.kind) {
      case "range": {
        const v = settings.get<number>(d.id);
        return h(Slider, { id, label: t(d.label), value: v, min: d.min, max: d.max, step: d.step, valueText: p.format ? p.format(d, v) : String(v), onChange: (x) => set(d.id, x) });
      }
      case "toggle":
        return h(Toggle, { id, label: t(d.label), on: settings.get<boolean>(d.id), onText: t(p.on), offText: t(p.off), onChange: (x) => set(d.id, x) });
      case "choice":
        return h(Choice, { id, label: t(d.label), value: settings.get<string>(d.id), options: d.options.map((o) => ({ value: o.value, label: t(o.label) })), onChange: (x) => set(d.id, x) });
    }
  };
  return h(
    "div",
    { "data-settings": "" },
    p.groups.map((g) => {
      const defs = settings.defs.filter((d) => d.group === g.id);
      return defs.length ? h(Group, { id: `settings-${g.id}`, title: t(g.label) }, defs.map(control)) : null;
    }),
  );
}
